import { Button, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, styled } from '@mui/material';
import React, { useState } from 'react';
import { GridContainer, Grid } from 'src/shared/grid';
import { useAddAlert } from 'src/store/actions';
import { useStoreContext } from 'src/store/context';
import DownloadIcon from '@mui/icons-material/Download';
import Select from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import { uploadFieldSampleMutation } from 'src/apis/mutationsFuelSample';
import LoadingSpinner from 'src/shared/loading/loading';
import fieldSampleTemplate from 'src/assets/Field-Sample-Template.csv';
import { alertMessages } from 'src/constants';
import { errorResponse } from 'src/store/common';

type DataUploadProps = {
    token: any,
    setOpen: any,
    open: boolean
    handleRefresh: any
}
const GIGABYTE = 1*1024*1024*1024;
const MEGABYTE = 1*1024*1024;
const KILOBYTE = 1*1024;
const ACCEPTED_FILE_TYPES = ['text/csv', 'application/vnd.ms-excel']
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });
export const DataUpload: React.FC<DataUploadProps> = ({ token, setOpen, open, handleRefresh }) => {
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [urlTemplate]= useState(fieldSampleTemplate);
	const [loading, setLoading] = useState<boolean> (false);
    const [,dispatch] = useStoreContext();
    const addAlert = useAddAlert(dispatch);
    
    const handleUploadedFiles = (files: File[])=>{
        setUploadedFiles(files);
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const chosenFiles = Array.prototype.slice.call(e.target.files)
            handleUploadedFiles(chosenFiles)
        }
    };

    const getFileSize = (fileSize:number) => {
        if (fileSize/GIGABYTE >= 1) return Number((fileSize/GIGABYTE).toFixed(1)) + ' GB'
        else if(fileSize/MEGABYTE >= 1) return Number((fileSize/MEGABYTE).toFixed(1)) + ' MB'
        else if (fileSize/KILOBYTE >= 1) return Number((fileSize/KILOBYTE).toFixed(1)) + ' KB'
        else if(fileSize/KILOBYTE < 1) return Number((fileSize).toFixed(1)) + ' bytes'
    }

    const handleSubmit = () => {
        setLoading(true);
        (async () => {
            try {
                for(const file of uploadedFiles) {
                    const fileContents = await new Response(file).text()
                    let request = {
                        fileName: file.name,
                        fileType: 'binary',
                        fileFormat: 'csv',
                        fileContents: fileContents,
                        fileSize: getFileSize(file.size),
                        addAlert: addAlert
                    }
                    await uploadFieldSampleMutation(token, addAlert, request);
                }
            } catch (error) {
                errorResponse(error, addAlert, alertMessages.getFiles);
                console.log('error', error);
            }
            setLoading(false);
            handleRefresh();
            setOpen(!open); 
        })();
    };

    return (
        <GridContainer className="removePadding">
            <Grid row gap="md" className='page-heading'>
                <Grid
                    desktop={{ col: 12 }}
                    className="display-flex flex-row flex-align-center flex-justify margin-bottom-205 paddingForModal">
                    <span className="font-sans-lg text-bold">Field Sample Data Import</span>
                </Grid>
            </Grid>
            <hr></hr>
            <Grid row gap="md">
                <Grid
                    tabIndex={0}
                    desktop={{ col: 12 }}
                    className="display-flex flex-row flex-align-center flex-justify"
                    style={{ marginLeft: '2rem', marginRight: '2rem' }}
                >
                    <div style={{ paddingLeft: '2rem', fontSize: '15px', width: '100%', paddingBottom: '1.5%' }} className="selectedStationTextBox">Uploaded CSV data needs to be correctly formatted.&nbsp;&nbsp;<a href={urlTemplate} download="Field-Sample-Template.csv" style={{textDecoration: 'underline', fontWeight: 'bold', color: '#1363BE'}}><DownloadIcon style={{fontSize: 'large'}}/>Download CSV Template</a>
                    </div>
                </Grid>

            </Grid>
            <Grid row gap="md">
                <Grid
                    desktop={{ col: 12 }}
                    className="display-flex flex-row flex-align-center flex-justify"
                    style={{ marginLeft: '3rem', marginRight: '2rem' }}
                >
                    <span className="font-sans-md text-bold">Upload File</span>
                </Grid>
            </Grid>
            <Grid row gap="md">
                <Grid
                    desktop={{ col: 12 }}
                    style={{ marginLeft: '3rem', marginRight: '2rem' }}
                >
                        <Button
                            component="label"
                            role={undefined}
                            sx={{textTransform: 'capitalize'}}
                            variant="outlined"
                            tabIndex={-1}
                        >
                        Select File
                        <VisuallyHiddenInput
                            type="file"
                            onChange={handleFileChange}
                            accept='.csv'
                        />
                        </Button>
                    <span style={{marginLeft: '1rem', fontStyle:'italic'}}>Max file size: 1 MB</span>
                </Grid>
            </Grid>

            {(uploadedFiles.length>0 && Number((uploadedFiles[0]?.size/MEGABYTE).toFixed(1)) > 1) && (
                <Grid row gap="md">
                    <Grid desktop={{ col: 12 }}style={{ marginLeft: '3rem', marginRight: '2rem' }}>
                        <span className="padding-bottom-2" style={{color: "rgb(211, 47, 47)"}}>File is {Number((uploadedFiles[0]?.size/MEGABYTE).toFixed(1))} MB.  File Must be less than 1 MB</span>
                    </Grid>
                </Grid>
            )}
            {(uploadedFiles.length>0 && !ACCEPTED_FILE_TYPES.includes(uploadedFiles[0]?.type)) && (
                <Grid row gap="md">
                    <Grid desktop={{ col: 12 }}style={{ marginLeft: '3rem', marginRight: '2rem' }}>
                        <span className="padding-bottom-2" style={{color: "rgb(211, 47, 47)"}}>File must be a CSV</span>
                    </Grid>
                </Grid>
            )}

            <Grid row gap="md">
                <Grid
                    desktop={{ col: 12 }}
                    className='smooth-scrollbar'
                    style={{ marginLeft: '3rem', marginRight: '2rem', maxHeight: '10rem' }}
                >
                    {
                        uploadedFiles?.length>0 && 
                        <TableContainer component={Paper} style={{marginBottom: '2rem'}}
                        className='margin-top-2 first-table MuiPaper-root-special'>
                        {(loading) && (
                            <div style={{height: '30%'}}>
                            <LoadingSpinner/>
                            </div>
                        )}
                            {/* TABLE FOR FUELS*/}
                            <Table
                                sx={{ minWidth: 200, minHeight: 100}}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell tabIndex={0} className="text-align-override headerParent" >FILE NAME</TableCell>
                                        <TableCell tabIndex={0} className="text-align-override headerParent" >FILE FORMAT</TableCell>
                                        <TableCell tabIndex={0} className="text-align-override headerParent" >SIZE</TableCell>
                                        <TableCell tabIndex={0} className="text-align-override headerParent" >
                                            <label id='uploadDataType' htmlFor='uploadDataType'>
                                                DATA TYPE
                                            </label>
                                        </TableCell>
                                        <TableCell tabIndex={0} className="text-align-override headerParent" component={"td"}>&nbsp;</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {uploadedFiles?.length>0 &&  uploadedFiles.map((row, index)=>{
                                        return(
                                            <TableRow key={`${row.name}-${index}`}>
                                                <TableCell tabIndex={0} className="text-align-override" >{row.name}</TableCell>
                                                <TableCell tabIndex={0} className="text-align-override">{ACCEPTED_FILE_TYPES.includes(row.type) ? 'CSV' : row.type}</TableCell>
                                                <TableCell tabIndex={0} className="text-align-override">{getFileSize(row.size)}</TableCell>
                                                <TableCell tabIndex={0} className="text-align-override">
                                                        <Select
                                                            displayEmpty
                                                            labelId='uploadDataType'
                                                            name="uploadDataType"
                                                            value={'Field Sample'}
                                                            className= "select-styles"
                                                            id="uploadDataType"
                                                        >
                                                            <MenuItem value="Field Sample" key="Field Sample">Field Sample</MenuItem>
                                                        </Select>
                                                </TableCell>
                                                <TableCell tabIndex={0} className="text-align-override">
                                                    <Tooltip title={ "Remove File" } placement="top-start">
                                                        <Button style={{ minWidth: '0', paddingRight: '2px', paddingLeft: '2px', color:'black',  height: '30px'}} 
                                                            onClick={() =>{setUploadedFiles([])}}>
                                                            <CloseIcon/>
                                                        </Button>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>                                                
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                </Grid>
            </Grid>

            <Grid row className='modalBottomDiv'>
                <Grid desktop={{ col: 10 }} className="modalCancelDiv">
                    <Button variant="outlined" style={{ backgroundColor: 'white' }} onClick={() => { setOpen(!open) }}>
                        Cancel
                    </Button>
                </Grid>
                <Grid desktop={{ col: 2 }} className='modalSubmitDiv'>
                    <Button variant="contained" 
                        type="submit" 
                        aria-disabled={
                            uploadedFiles?.length<1 || (uploadedFiles.length>0 && Number((uploadedFiles[0]?.size/MEGABYTE).toFixed(1)) > 1) || (uploadedFiles.length>0 && !ACCEPTED_FILE_TYPES.includes(uploadedFiles[0]?.type))
                        } onClick={()=>{handleSubmit()}}>
                        Upload
                    </Button>
                </Grid>

            </Grid>
        </GridContainer>
    )
}